import {
  inject,
  Injectable,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';

import { filter, map, Subject, takeUntil } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';

import { NgGencatVerticalMenuItem } from '@shared/models/app-config.model';
import { SIDEBAR_APP_CONFIG } from '@core/configurations/sidebar-app.config';

@Injectable({
  providedIn: 'root',
})
export class SidebarService implements OnDestroy {
  sidebarSignalState: WritableSignal<NgGencatVerticalMenuItem[]> = signal(
    SIDEBAR_APP_CONFIG.menuItems,
  );

  private readonly _subscription = new Subject<void>();
  private readonly _router = inject(Router);

  constructor() {
    this.readSidebarMainRoute();
  }

  ngOnDestroy(): void {
    this._subscription.complete();
    this._subscription.unsubscribe();
  }

  private readSidebarMainRoute(): void {
    this._router.events
      .pipe(takeUntil(this._subscription))
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(({ url }: NavigationEnd) => url),
      )
      .subscribe((urlEvent: string) => {
        const splitCurrentUrl: string[] = urlEvent.split('/');

        if (splitCurrentUrl.length > 2) {
          this.sidebarSignalState.set(
            this.setDropdDownState(splitCurrentUrl, false),
          );
        } else {
          this.sidebarSignalState.set(
            this.setDropdDownState(splitCurrentUrl, true),
          );
        }
      });
  }

  private setDropdDownState(
    splitCurrentUrl: string[],
    isFirstLayer: boolean,
  ): NgGencatVerticalMenuItem[] {
    return this.sidebarSignalState().map((item: NgGencatVerticalMenuItem) => {
      const urlParent: string = `/${splitCurrentUrl[1]}`;
      const setParentUrl: string = urlParent === '/' ? '/home' : urlParent;

      //Set parent dropdown state
      if (item.href === setParentUrl) {
        return {
          ...item,
          isOpen: item.isDropdown ? true : false,
          isActive: true,
          dropdownContent: item.isDropdown
            ? this.setDropdownContent(item, splitCurrentUrl, isFirstLayer)
            : [],
        };
      }

      return {
        ...item,
        isOpen: false,
        isActive: false,
        dropdownContent: item.isDropdown
          ? this.setDropdownContent(item, splitCurrentUrl, isFirstLayer)
          : [],
      };
    });
  }

  private setDropdownContent(
    item: NgGencatVerticalMenuItem,
    splitCurrentUrl: string[],
    isFirstLayer: boolean,
  ): NgGencatVerticalMenuItem[] | undefined {
    return item.dropdownContent?.map((child: NgGencatVerticalMenuItem) => {
      if (!!splitCurrentUrl.find((url: string) => url === child.lastPath)) {
        return {
          ...child,
          isOpen: isFirstLayer ? false : true,
          isActive: isFirstLayer ? false : true,
          dropdownContent: child.isDropdown
            ? this.setDropdownContent(child, splitCurrentUrl, isFirstLayer)
            : [],
        } as NgGencatVerticalMenuItem;
      }

      return {
        ...child,
        isOpen: false,
        isActive: false,
        dropdownContent: child.isDropdown
          ? this.setDropdownContent(child, splitCurrentUrl, isFirstLayer)
          : [],
      } as NgGencatVerticalMenuItem;
    });
  }
}
